import { Admin } from '../types/admin';
import axiosInstance from '../api/apiInterceptor';
import { AxiosResponse } from 'axios';
import { TEACHER_API_ENDPOINT, Admin_API_ENDPOINT_WITH_FILTER } from "../../../constants/constants"
import { getUserDetails } from '../../../lib/getUserDetails';
import { getUserToken } from '../../../lib/getUserToken';

interface Payload {
  userId: number;
}

// Updated interfaces for geo-units with correct structure
interface State {
  id: number;
  name: string;
  code: string | null;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface District {
  id: number;
  stateId: number;
  name: string;
  code: string;
  isDeleted: boolean;
  createdBy: number;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string | null;
}

interface Block {
  id: number;
  districtId: number;
  name: string;
  isDeleted: boolean;
  createdBy: number;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string | null;
}

interface Village {
  id: number;
  blockAreaId: number; // Changed from blockId to blockAreaId
  name: string;
  isDeleted: boolean;
  createdBy: number;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string | null;
}

// NEW: School interface
interface School {
  id: number;
  name: string;
  schoolCode: string;
  udiseCode: string;
  address: string;
  phone: string;
  shift: string;
  gender: string;
  hmId: string;
  isActive: boolean;
  isDeleted: boolean;
  villageWardId: number;
  blockAreaId: number;
  districtId: number;
  stateId: number;
  schoolType: string;
  locationType: string;
  areaType: string;
  kitchenType: string;
}

interface ApiResponse<T> {
  statusCode: number;
  status: string;
  message: string;
  total: number;
  data: T[];
}

// User Detail Interface with correct structure
interface UserRole {
  role: string;
  referenceKey: string;
  relatedEntity: {
    states?: State;
    districts?: District;
    blockAreas?: Block; // Changed from blocks to blockAreas
    villages?: Village;
  };
}

interface UserToken {
  userId: number;
  phone: string;
  roleIds: number[];
}

const extractErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  
  if (error?.response?.data?.details) {
    return error.response.data.details;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};

class ApiError extends Error {
  public originalError: any;
  public statusCode?: number;
  
  constructor(message: string, originalError: any) {
    super(message);
    this.name = 'ApiError';
    this.originalError = originalError;
    this.statusCode = originalError?.response?.status;
  }
}

// Get current user details using your functions
export const getCurrentUserDetails = (): UserRole[] => {
  try {
    const userDetails = getUserDetails();
    return userDetails || [];
  } catch (error) {
    console.error('Error getting user details:', error);
    return [];
  }
};

export const getCurrentUserToken = (): UserToken | null => {
  try {
    const userToken = getUserToken();
    return userToken || null;
  } catch (error) {
    console.error('Error getting user token:', error);
    return null;
  }
};

// UPDATED: Get current user's role type based on your tier system (added schoolHm support)
export const getCurrentUserRoleType = (): 'admin' | 'state' | 'district' | 'block' | 'villageWard' | 'schoolHm' | null => {
  const userDetails = getCurrentUserDetails();
  if (!userDetails || userDetails.length === 0) {
    return null;
  }
  
  const role = userDetails[0].role.toLowerCase();
  if (role.includes('schoolhm') || role.includes('headmaster')) return 'schoolHm';
  if (role.includes('villageward') || role.includes('village')) return 'villageWard';
  if (role.includes('district')) return 'district';
  if (role.includes('state')) return 'state';
  if (role.includes('block')) return 'block';
  if (role.includes('admin') && !role.includes('district') && !role.includes('state')) return 'admin';
  return null;
};

// Get current user's related entity
export const getCurrentUserEntity = (): any => {
  const userDetails = getCurrentUserDetails();
  if (!userDetails || userDetails.length === 0) {
    return null;
  }
  
  return userDetails[0].relatedEntity;
};

// Get current user's role ID
export const getCurrentUserRoleId = (): number | null => {
  const userToken = getCurrentUserToken();
  if (!userToken || !userToken.roleIds || userToken.roleIds.length === 0) {
    return null;
  }
  return userToken.roleIds[0];
};

export const fetchAdminDetailApi = async (payload: Payload): Promise<Admin[]> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get(`${TEACHER_API_ENDPOINT}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching Teachers:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

// UPDATED: Added support for schoolHm filtering
export const fetchAdminApi = async (): Promise<Admin[]> => {
  try {
    const userRoleType = getCurrentUserRoleType();
    const userEntity = getCurrentUserEntity();
    
    let url = `${Admin_API_ENDPOINT_WITH_FILTER}?filter=roleIds|1-2-3-4-12&limit=1000`;
    
    if (userRoleType === 'state' && userEntity && userEntity.states) {
      url += `&stateFilter=${userEntity.states.id}`;
    } 
    else if (userRoleType === 'district' && userEntity && userEntity.districts) {
      url += `&districtFilter=${userEntity.districts.id}`;
    }
    else if (userRoleType === 'block' && userEntity && userEntity.blockAreas) {
      // Block admin can only see village ward admins
      url = `${Admin_API_ENDPOINT_WITH_FILTER}?filter=roleIds|12&limit=1000&blockFilter=${userEntity.blockAreas.id}`;
    }
    else if (userRoleType === 'villageWard' && userEntity && userEntity.villageWards) {
      // NEW: Village admin can only see headmasters in their village
      url = `${Admin_API_ENDPOINT_WITH_FILTER}?filter=roleIds|7,villageWardId|${userEntity.villageWards.id}&limit=1000`;
    }
    else {
      // Admin sees all except village ward admins and headmasters
      url = `${Admin_API_ENDPOINT_WITH_FILTER}?filter=roleIds|1-2-3&limit=1000`;
    }
    
    const response: AxiosResponse<any> = await axiosInstance.get(url);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching Admin:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

// UPDATED: Added schoolHm support for village admins
export const fetchAdminRoleApi = async () => {
  try {
    const userRoleType = getCurrentUserRoleType();

    let filterQuery = 'admin-state-district'; 
    
    if (userRoleType === 'state') {
      filterQuery = 'district-block';
    } else if (userRoleType === 'district') {
      filterQuery = 'block-villageWard';
    } else if (userRoleType === 'block') {
      filterQuery = 'villageWard';
    } else if (userRoleType === 'villageWard') {
      // NEW: Village admin can create headmasters
      filterQuery = 'schoolHm';
    } else if (userRoleType === 'admin') {
      filterQuery = 'admin-state-district';
    }
    
    const url = `user-roles?filter=tiers|${filterQuery}`;
    const response: AxiosResponse<any> = await axiosInstance.get(url);
    
    return response.data;
  } catch (error: any) {
    console.log('Error fetch user role:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
}

// NEW: School API functions
export const fetchSchoolsApi = async (villageWardId?: number): Promise<School[]> => {
  try {
    const userRoleType = getCurrentUserRoleType();
    const userEntity = getCurrentUserEntity();
    
    let url = 'schools';
    
    if (userRoleType === 'villageWard' && userEntity && userEntity.villageWards) {
      url += `?filter=villageWardId|${userEntity.villageWards.id}&limit=1000`;
    } else if (villageWardId) {
      url += `?filter=villageWardId|${villageWardId}&limit=1000`;
    } else {
      url += '?limit=1000';
    }
    
    const response: AxiosResponse<ApiResponse<School>> = await axiosInstance.get(url);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching schools:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const fetchSchoolsByVillageWardApi = async (villageWardId: number): Promise<School[]> => {
  try {
    const userRoleType = getCurrentUserRoleType();
    const userEntity = getCurrentUserEntity();
    
    if (userRoleType === 'villageWard' && userEntity && userEntity.villageWards) {
      if (villageWardId !== userEntity.villageWards.id) {
        throw new ApiError('Unauthorized: Cannot access schools outside your village', null);
      }
    }
    
    const response: AxiosResponse<ApiResponse<School>> = await axiosInstance.get(`schools?filter=villageWardId|${villageWardId}&limit=1000`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching schools by village ward:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

// Existing geo-unit API functions (unchanged)
export const fetchStatesApi = async (): Promise<State[]> => {
  try {
    const userRoleType = getCurrentUserRoleType();
    const userEntity = getCurrentUserEntity();
    
    let url = `geo-units/states?filter=&limit=100`;

    if (userRoleType === 'state' && userEntity && userEntity.states) {
      url += `?filter=id|${userEntity?.states?.id}`;
    }
    
    const response: AxiosResponse<ApiResponse<State>> = await axiosInstance.get(url);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching states:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const fetchDistrictsApi = async (stateId?: number): Promise<District[]> => {
  try {
    const userRoleType = getCurrentUserRoleType();
    const userEntity = getCurrentUserEntity();
    
    let url = 'geo-units/districts';
    
    if (userRoleType === 'state' && userEntity && userEntity.states) {
      url += `?filter=stateId|${userEntity.states.id}`;
    } else if (userRoleType === 'district' && userEntity && userEntity.districts) {
      url += `?filter=id|${userEntity.districts.id}`;
    } else if (stateId) {
      url += `?filter=stateId|${stateId}`;
    }
    
    const response: AxiosResponse<ApiResponse<District>> = await axiosInstance.get(url);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching districts:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const fetchDistrictsByStateApi = async (stateId: number): Promise<District[]> => {
  try {
    const userRoleType = getCurrentUserRoleType();
    const userEntity = getCurrentUserEntity();
    
    if (userRoleType === 'state' && userEntity && userEntity.states) {
      if (stateId !== userEntity.states.id) {
        throw new ApiError('Unauthorized: Cannot access districts outside your state', null);
      }
    }
    
    const response: AxiosResponse<ApiResponse<District>> = await axiosInstance.get(`geo-units/districts?filter=stateId|${stateId}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching districts by state:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

// Block API functions
export const fetchBlocksApi = async (districtId?: number): Promise<Block[]> => {
  try {
    const userRoleType = getCurrentUserRoleType();
    const userEntity = getCurrentUserEntity();
    
    let url = 'geo-units/blocks';
    if (userRoleType === 'district' && userEntity && userEntity.districts) {
      url += `?filter=districtId|${userEntity.districts.id}`;
    } else if (userRoleType === 'block' && userEntity && userEntity.blockAreas) {
      // Block admin can only see their own block
      url += `?filter=id|${userEntity.blockAreas.id}`;
    } else if (districtId) {
      url += `?filter=districtId|${districtId}`;
    }
    
    const response: AxiosResponse<ApiResponse<Block>> = await axiosInstance.get(url);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching blocks:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const fetchBlocksByDistrictApi = async (districtId: number): Promise<Block[]> => {
  try {
    const userRoleType = getCurrentUserRoleType();
    const userEntity = getCurrentUserEntity();
    if (userRoleType === 'district' && userEntity && userEntity.districts) {
      if (districtId !== userEntity.districts.id) {
        throw new ApiError('Unauthorized: Cannot access blocks outside your district', null);
      }
    }
    
    const response: AxiosResponse<ApiResponse<Block>> = await axiosInstance.get(`geo-units/blocks?filter=districtId|${districtId}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching blocks by district:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

// Updated Village API functions with correct blockAreaId filter
export const fetchVillagesApi = async (blockAreaId?: number): Promise<Village[]> => {
  try {
    const userRoleType = getCurrentUserRoleType();
    const userEntity = getCurrentUserEntity();
    
    let url = 'geo-units/villages';
    if (userRoleType === 'block' && userEntity && userEntity.blockAreas) {
      // Use blockAreaId filter for villages
      url += `?filter=blockAreaId|${userEntity.blockAreas.id}`;
    } else if (blockAreaId) {
      url += `?filter=blockAreaId|${blockAreaId}`;
    }
    const response: AxiosResponse<ApiResponse<Village>> = await axiosInstance.get(url);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching villages:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const fetchVillagesByBlockAreaApi = async (blockAreaId: number): Promise<Village[]> => {
  try {
    const userRoleType = getCurrentUserRoleType();
    const userEntity = getCurrentUserEntity();
    
    if (userRoleType === 'block' && userEntity && userEntity.blockAreas) {
      if (blockAreaId !== userEntity.blockAreas.id) {
        throw new ApiError('Unauthorized: Cannot access villages outside your block area', null);
      }
    }
    
    const response: AxiosResponse<ApiResponse<Village>> = await axiosInstance.get(`geo-units/villages?filter=blockAreaId|${blockAreaId}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching villages by block area:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

// Legacy function name for backward compatibility
export const fetchVillagesByBlockApi = fetchVillagesByBlockAreaApi;

// Get user assigned role API for fetching uarId
export const getUserAssignedRoleApi = async (userId: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get(`user-assigned-roles?filter=roleIds|7,userId|${userId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching user assigned role:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const addAdminApi = async (AdminData: Omit<Admin, 'id'>): Promise<Admin> => {
  try {
    const response: AxiosResponse<Admin> = await axiosInstance.post(TEACHER_API_ENDPOINT, AdminData);
    return response.data;
  } catch (error: any) {
    console.error('Error adding Admin:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const updateAdminApi = async (Admin: Admin): Promise<Admin> => {
  try {
    const response: AxiosResponse<Admin> = await axiosInstance.patch(`${TEACHER_API_ENDPOINT}/${Admin.id}`, Admin);
    return response.data;
  } catch (error: any) {
    console.error('Error updating Admin:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const updateUserAssignedRoleApi = async (uarId: number, roleId: number, parameter?: number): Promise<any> => {
  try {
    const requestData: any = {
      roleId: roleId
    };
    
    // Add parameter if provided (for state/district/block/village/school admins)
    if (parameter !== undefined && parameter !== null) {
      requestData.parameter = parameter;
    }
    
    const response: AxiosResponse<any> = await axiosInstance.patch(
      `user-assigned-roles/${uarId}`, 
      requestData
    );
    return response.data;
  } catch (error: any) {
    console.error('Error updating user assigned role:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

// NEW: Update school API to assign headmaster
export const updateSchoolHeadmasterApi = async (schoolId: number, hmId: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch(`schools/${schoolId}`, {
      hmId: hmId
    });
    return response.data;
  } catch (error: any) {
    console.error('Error updating school headmaster:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const deleteAdminApi = async (id: number): Promise<number> => {
  try {
    await axiosInstance.delete(`${TEACHER_API_ENDPOINT}/${id}`);
    return id;
  } catch (error: any) {
    console.error('Error deleting Admin:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};